import { graphql } from 'react-apollo';

import type { EnterpriseContractUtilizations_UtilizationEdge } from '__generated__/graphql-types-do-not-use';
import Uri from 'jsuri';
import { filter, find, map, sortBy } from 'lodash';
import Q from 'q';
import { compose, withProps } from 'recompose';

import API from 'js/lib/api';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'js/l... Remove this comment to see the full error message
import getPropsFromPromise from 'js/lib/getPropsFromPromise';

import type { DecoratedEnterpriseContract, PreviousPayment } from 'bundles/enterprise-admin-account/constants/types';
import EnterpriseContractUtilizations from 'bundles/enterprise-admin-account/queries/EnterpriseContractUtilizationsQuery.graphql';
import type {
  EnterpriseContractUtilizationsQuery as EnterpriseContractUtilizationsQueryType,
  EnterpriseContractUtilizationsQueryVariables,
} from 'bundles/enterprise-admin-account/queries/__generated__/EnterpriseContractUtilizationsQuery';
import { enterpriseContractQuery } from 'bundles/enterprise-admin-account/utils/AdminDashboardGraphqlQuery';
import type {
  enterpriseContractQuery as enterpriseContractQueryData,
  enterpriseContractQueryVariables,
} from 'bundles/enterprise-admin-account/utils/__generated__/enterpriseContractQuery';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import DecoratedEnterpriseContractsV1 from 'bundles/naptimejs/resources/decoratedEnterpriseContracts.v1';
import type EnterpriseContractDeferredLicenseChangesV1 from 'bundles/naptimejs/resources/enterpriseContractDeferredLicenseChanges.v1';

type PropsFromCaller = {
  thirdPartyOrganization: { id: string };
};

type PropsFromDecoratedEnterpriseContractsQuery = {
  contracts: DecoratedEnterpriseContractsV1[];
  contractsLoading: boolean;
  refetchContracts?: () => void;
};

type InvoiceAmount = {
  currencyCode: string;
  units: string;
  nanos: number;
};

type Invoice = {
  amount: InvoiceAmount;
  invoiceState: string;
  invoiceType: string;
  paymentMethod: string;
  invoiceId: string;
  contractId: string;
  dueTime: string;
  sentTime: string;
  stateLastChangedTime: string;
  thirdPartyOrganizationId: string;
  creatorUserId: string;
  numSeats: number;
  hostedInvoiceUrl: string;
  invoicePdfUrl: string;
  externalInvoiceId: string;
  invoiceNumber: string;
  stateJson: string; // Consider parsing it into a detailed type if needed
};

type InvoiceWrapper = {
  invoice: Invoice;
  invoiceId: string;
};

type InvoiceList = InvoiceWrapper[];

type PropsFromEnterpriseContractUtilization = {
  contractUtilizations?: EnterpriseContractUtilizations_UtilizationEdge[] | null;
  contractUtilizationsLoading: boolean;
};

export type PropsFromEnhancer = {
  decoratedContracts: DecoratedEnterpriseContract[];
  contractsLoading: boolean;
  refetchContracts?: () => void;
} & PropsFromEnterpriseContractUtilization;

const withDecoratedEnterpriseContractsWithInvoices = compose<PropsFromEnhancer, PropsFromCaller>(
  graphql<
    PropsFromCaller,
    enterpriseContractQueryData,
    enterpriseContractQueryVariables,
    PropsFromDecoratedEnterpriseContractsQuery
  >(enterpriseContractQuery, {
    options: ({ thirdPartyOrganization: { id } }) => ({
      variables: { id },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }),
    props: ({ data }) => {
      if (data) {
        const { refetch } = data;
        const elements = data?.DecoratedEnterpriseContractsV1Resource?.byThirdPartyOrganization?.elements;
        return {
          contracts: elements?.map((x) => new DecoratedEnterpriseContractsV1(x)) || [],
          contractsLoading: data.loading,
          refetchContracts: refetch,
        };
      } else {
        return {
          contracts: [],
          contractsLoading: true,
        };
      }
    },
  }),
  graphql<
    PropsFromDecoratedEnterpriseContractsQuery,
    EnterpriseContractUtilizationsQueryType,
    EnterpriseContractUtilizationsQueryVariables,
    PropsFromEnterpriseContractUtilization
  >(EnterpriseContractUtilizations, {
    options: ({ contracts, contractsLoading }) => ({
      variables: {
        contractIds: contracts.map((contract) => contract.enterpriseContractId),
      },
      skip: !contracts.map((contract) => contract.enterpriseContractId).length || contractsLoading,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      context: {
        clientName: 'gatewayGql',
      },
    }),
    props: ({ data }) => {
      return {
        contractUtilizations: data?.EnterpriseContractUtilizations?.queryByContractIds?.edges,
        contractUtilizationsLoading: data?.loading || false,
      };
    },
  }),
  getPropsFromPromise((props: Record<string, DecoratedEnterpriseContractsV1[]>) => {
    const { contracts } = props;
    const enterpriseContractInvoiceAPI = API(
      '/api/grpc/enterprisecontractinvoice/v1beta1/EnterpriseContractInvoiceAPI/ByContractId',
      {
        type: 'rest',
      }
    );
    if (props.contractUtilizationsLoading) return Q.resolve();
    const contractsToFetch = contracts.map((contract) => {
      const invoiceUri = new Uri().addQueryParam('contract_id', contract.id);
      return Q<{ contractInvoices: InvoiceList }>(enterpriseContractInvoiceAPI.post(invoiceUri.toString(), {}));
    });

    return Q.all(contractsToFetch).then((responses) => {
      return contracts.reduce((result, contract, index) => {
        result[`contract~${contract.id}`] = responses[index].contractInvoices.map((item) => item.invoice);
        return result;
      }, {} as Record<string, Invoice[]>);
    });
  }),
  withProps(
    (
      props: PropsFromDecoratedEnterpriseContractsQuery &
        Record<string, (Invoice | EnterpriseContractDeferredLicenseChangesV1 | undefined)[]>
    ) => {
      const contracts: DecoratedEnterpriseContractsV1[] = props.contracts;
      const mappedContracts = contracts.map((contract) => {
        const contractInvoices = props[`contract~${contract.id}`] as unknown as Invoice[];

        const paidInvoiceIds = contract.previousPayments.map((payment: PreviousPayment) => payment.invoiceId);

        // Synthetically creating "previousPayments" with unpaid invoices from a separate API.
        // We concat them in later and sort to simulate it coming from a single data source.
        const unpaidInvoices = contractInvoices || ([] as InvoiceList);

        const filteredUnpaidInvoices = filter(unpaidInvoices, (invoice) => {
          if (
            invoice &&
            invoice.invoiceId &&
            invoice.invoiceState === 'ENTERPRISE_CONTRACT_INVOICE_STATE_AWAITING_PAYMENT' &&
            paidInvoiceIds.indexOf(invoice?.invoiceId) === -1
          ) {
            return true;
          }
          return false;
        });

        const mappedFilteredUnpaidInvoices = map(filteredUnpaidInvoices, (invoice) => {
          if (!invoice) {
            console.warn('Encountered an undefined or null invoice.');
            return null; // Skip invalid invoices
          }

          const {
            amount,
            invoiceId: id,
            sentTime: sentAt,
            invoiceType,
            invoiceState,
            dueTime: dueAt,
            hostedInvoiceUrl,
            invoicePdfUrl,
          } = invoice;

          const formattedAmount = amount
            ? `${parseInt(amount.units, 10)}.${String(amount.nanos || 0)
                .padStart(9, '0')
                .slice(0, 2)}`
            : null;

          const formattedInvoiceType = invoiceType?.split('_').pop() || null;
          const formattedInvoiceState = invoiceState?.split('_').pop() || null;

          return {
            amount: formattedAmount,
            currencyCode: amount ? amount.currencyCode : null,
            invoiceId: id || null,
            paymentAt: sentAt ? new Date(sentAt).getTime() : null,
            paymentType: formattedInvoiceType,
            invoiceState: formattedInvoiceState,
            dueAt: dueAt ? new Date(dueAt).getTime() : null,
            hostedInvoiceUrl: hostedInvoiceUrl || null,
            invoicePdfUrl: invoicePdfUrl || null,
          };
        }).filter(Boolean); // Remove null items
        const previousPayments = contract.previousPayments.map(
          (payment: { invoiceId: string; paymentAt?: number; paymentDate?: number }) => {
            const { invoiceId } = payment;
            if (!invoiceId) {
              return payment;
            }

            const invoice = find(contractInvoices, (inv) => inv?.invoiceId === payment.invoiceId);
            const invoiceState = invoice?.invoiceState;

            return {
              ...payment,
              invoiceState,
              invoice,
            };
          }
        );
        const sortedPayments = sortBy([...previousPayments, ...mappedFilteredUnpaidInvoices], (val) => {
          return val.paymentAt || val.paymentDate;
        });

        return new DecoratedEnterpriseContractsV1({
          ...contract,
          previousPayments: sortedPayments,
        });
      });

      return { decoratedContracts: mappedContracts };
    }
  )
);

export default withDecoratedEnterpriseContractsWithInvoices;
